import React from 'react';
import { Card, CardContent, Typography, TextField, Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { VitalsData } from '@app-types/visit';
import { LocalHospital } from '@mui/icons-material';

interface VitalsCardProps {
  vitals: VitalsData;
  onChange: (vitals: VitalsData) => void;
  disabled?: boolean;
}

const VitalsCard: React.FC<VitalsCardProps> = React.memo(({ vitals, onChange, disabled = false }) => {
  const updateField = <K extends keyof VitalsData>(field: K, value: VitalsData[K]) => {
    onChange({ ...vitals, [field]: value });
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LocalHospital sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Vitale / Triage</Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="T° (°C)"
              type="number"
              value={vitals.temperature ?? ''}
              onChange={(e) => updateField('temperature', e.target.value ? Number(e.target.value) : undefined)}
              disabled={disabled}
              inputProps={{ min: 35, max: 42, step: 0.1 }}
              helperText="Normal: 37.5-39°C"
            />
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="FC (bpm)"
              type="number"
              value={vitals.heartRate ?? ''}
              onChange={(e) => updateField('heartRate', e.target.value ? Number(e.target.value) : undefined)}
              disabled={disabled}
              inputProps={{ min: 40, max: 250 }}
              helperText="Normal: 60-140"
            />
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="FR (rpm)"
              type="number"
              value={vitals.respiratoryRate ?? ''}
              onChange={(e) => updateField('respiratoryRate', e.target.value ? Number(e.target.value) : undefined)}
              disabled={disabled}
              inputProps={{ min: 10, max: 80 }}
              helperText="Normal: 10-30"
            />
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="CRT (s)"
              type="number"
              value={vitals.crt ?? ''}
              onChange={(e) => updateField('crt', e.target.value ? Number(e.target.value) : undefined)}
              disabled={disabled}
              inputProps={{ min: 0, max: 5, step: 0.1 }}
              helperText="Normal: <2s"
            />
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Durere (0-10)"
              type="number"
              value={vitals.painScore ?? ''}
              onChange={(e) => updateField('painScore', e.target.value ? Number(e.target.value) : undefined)}
              disabled={disabled}
              inputProps={{ min: 0, max: 10 }}
              helperText="0=fără, 10=severă"
            />
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="BCS (1-9)"
              type="number"
              value={vitals.bcs ?? ''}
              onChange={(e) => updateField('bcs', e.target.value ? Number(e.target.value) : undefined)}
              disabled={disabled}
              inputProps={{ min: 1, max: 9 }}
              helperText="Ideal: 4-5"
            />
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Greutate (kg)"
              type="number"
              value={vitals.weight ?? ''}
              onChange={(e) => updateField('weight', e.target.value ? Number(e.target.value) : undefined)}
              disabled={disabled}
              inputProps={{ min: 0, max: 200, step: 0.1 }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="Mucoase"
              value={vitals.mucosa ?? ''}
              onChange={(e) => updateField('mucosa', e.target.value)}
              disabled={disabled}
              placeholder="ex: roz, palide, icterice"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
});

VitalsCard.displayName = 'VitalsCard';

export default VitalsCard;
